import type { QrSymbol } from '../format/qr';

import { serialMm, tagSizes, type HangTag, type TagSize } from './hangTag';
import { paperColumns, qrShare, type ReceiptLine, type ReceiptPaper } from './receipt';

/**
 * The thermal receipt, the thermal invoice and the hang tag as one HTML page each, at the paper's
 * own size — what the phone turns into a PDF to send on WhatsApp and hands to the phone's print
 * dialog.
 *
 * <p>
 * <strong>HTML because a browser engine shapes every script.</strong> A customer's name in
 * Gujarati prints correctly here, where a thermal printer's text mode cannot print it at all; the
 * PDF and the system print both render through the phone's web engine, which is the same reason the
 * website's A4 sheets are HTML.
 * </p>
 *
 * <p>
 * <strong>The lines are the layouts' own</strong> (`receipt.ts`, `thermalInvoice.ts`), already
 * padded to 32 or 48 columns, so the PDF wraps exactly where the roll and the on-screen preview do.
 * Only the font size is chosen here, so that many columns fill the paper.
 * </p>
 */

/** A page ready for `expo-print`: its HTML and its size in points (72 a inch). */
export interface PrintPage {
  html: string;
  widthPt: number;
  heightPt: number;
}

const paperWidthMm: Record<ReceiptPaper, number> = { '58mm': 58, '80mm': 80 };
const marginMm = 3;

export const mmToPt = (mm: number) => (mm * 72) / 25.4;

/** Everything a person typed — a name, an address, a note — is text, never markup. */
export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * A monospace face carried inside the page, as data URLs of its two weights.
 *
 * <p>
 * <strong>Why the page carries its own font</strong> (2026-10-15): the roll layout lines figures up
 * with spaces, which only works when every character is the same width. Several phone makers ship a
 * system font that replaces the monospace one too, so the browser engine that draws the PDF fell
 * back to a proportional face — the columns drifted and the rules ran off the edge. The phone loads
 * the font it bundles and passes it here; without one the page falls back as before.
 * </p>
 */
export interface EmbeddedMonoFont {
  regular: string;
  bold: string;
}

/** A receipt or invoice as a roll-width page, one line of the layout to one line of the page. */
export function thermalPage(
  lines: readonly ReceiptLine[],
  paper: ReceiptPaper,
  font?: EmbeddedMonoFont | null,
): PrintPage {
  const widthMm = paperWidthMm[paper];
  // A monospace glyph is about 0.6 of its size wide; the 0.98 keeps the last column off the edge.
  const fontMm = ((widthMm - 2 * marginMm) / paperColumns[paper] / 0.6) * 0.98;
  const lineMm = fontMm * 1.35;
  const qrMm = (widthMm - 2 * marginMm) * qrShare;
  const contentMm = lines.reduce(
    (sum, line) => sum + (line.qr ? qrMm : (line.double ? 2 : 1) * lineMm),
    0,
  );
  const heightMm = contentMm + 2 * marginMm + 4;

  const body = lines
    .map((line) => {
      if (line.qr) {
        return `<div class="q">${qrSvg(line.qr)}</div>`;
      }

      const classes = ['l', line.bold ? 'b' : '', line.double ? 'd' : ''].filter(Boolean).join(' ');

      return `<div class="${classes}">${escapeHtml(line.text) || '&nbsp;'}</div>`;
    })
    .join('');

  const faces = font
    ? `@font-face { font-family: 'ReceiptMono'; font-weight: 400; src: url('${font.regular}'); }
    @font-face { font-family: 'ReceiptMono'; font-weight: 700; src: url('${font.bold}'); }`
    : '';

  const css = `${faces}
    @page { size: ${widthMm}mm ${heightMm.toFixed(2)}mm; margin: 0; }
    html, body { margin: 0; padding: 0; background: #fff; }
    body { padding: ${marginMm}mm; color: #000;
           font-family: 'ReceiptMono', Menlo, 'Courier New', monospace; }
    .l { white-space: pre; overflow: hidden; font-size: ${fontMm.toFixed(3)}mm;
         line-height: ${lineMm.toFixed(3)}mm; height: ${lineMm.toFixed(3)}mm; }
    .d { font-size: ${(fontMm * 2).toFixed(3)}mm; line-height: ${(lineMm * 2).toFixed(3)}mm;
         height: ${(lineMm * 2).toFixed(3)}mm; }
    .b { font-weight: 700; }
    .q { width: ${widthMm - 2 * marginMm}mm; height: ${qrMm.toFixed(3)}mm; display: flex;
         justify-content: center; }
    .q .qr { width: ${qrMm.toFixed(3)}mm; height: ${qrMm.toFixed(3)}mm; display: block; }`;

  return {
    html: page(css, body),
    widthPt: mmToPt(widthMm),
    heightPt: mmToPt(heightMm),
  };
}

/** The most copies of one tag a print asks for at once. */
export const maxTagCopies = 20;

/**
 * The hang tag at its real size, the QR as vector squares so it stays sharp at any print size —
 * `copies` of it, one to a page of tag stock, held between one and {@link maxTagCopies}.
 */
export function hangTagPage(tag: HangTag, size: TagSize, copies = 1): PrintPage {
  const count = Math.min(maxTagCopies, Math.max(1, Math.floor(copies)));

  return hangTagsPage(
    Array.from({ length: count }, () => tag),
    size,
  );
}

/**
 * Several garments' tags in one print, one to a page of tag stock — a delivery's, straight after it
 * is received. Each tag sizes its own code (`serialMm`), since one delivery's codes differ in
 * length.
 */
export function hangTagsPage(tags: readonly HangTag[], size: TagSize): PrintPage {
  const { widthMm, heightMm } = tagSizes[size];
  const qrMm = widthMm * 0.55;

  const css = `
    @page { size: ${widthMm}mm ${heightMm}mm; margin: 0; }
    html, body { margin: 0; padding: 0; background: #fff; }
    body { color: #000; font-family: -apple-system, Roboto, 'Noto Sans Gujarati', sans-serif; }
    .tag { width: ${widthMm}mm; height: ${heightMm}mm; box-sizing: border-box; padding: 3mm;
           display: flex; flex-direction: column; align-items: center; overflow: hidden;
           break-after: page; page-break-after: always; }
    .tag:last-child { break-after: auto; page-break-after: auto; }
    .hole { width: 4mm; height: 4mm; border-radius: 2mm; border: 0.2mm solid #888; margin-bottom: 2mm; }
    .qr { width: ${qrMm}mm; height: ${qrMm}mm; }
    .serial { font-family: Menlo, 'Courier New', monospace; font-weight: 700; margin-top: 1mm;
              text-align: center; }
    .serial .rn { white-space: nowrap; }
    .rule { align-self: stretch; height: 0.25mm; background: #000; margin: 1.5mm 0; }
    .design { font-weight: 700; font-size: 3.2mm; line-height: 3.9mm; text-align: center; }
    .variant { font-size: 2.6mm; text-align: center; }
    .rate { align-self: stretch; display: flex; justify-content: space-between; font-size: 2.5mm; }
    .rate b { font-size: 2.6mm; }
    .more { font-size: 2.1mm; text-align: center; }`;

  const body = tags
    .map((tag) => {
      const rate =
        tag.rate.kind === 'lines'
          ? `<div class="rule"></div>${tag.rate.lines
              .map(
                (line) =>
                  `<div class="rate"><span>${escapeHtml(line.label)}</span><b>${escapeHtml(line.value)}</b></div>`,
              )
              .join('')}${tag.rate.more ? '<div class="more">More rates at the counter</div>' : ''}`
          : '';
      // The shop's number never splits: a code too long for one line breaks before "(RN-7)",
      // never inside it at its hyphen.
      const reference = tag.code.slice(tag.serial.length).trim();
      const code = reference
        ? `${escapeHtml(tag.serial)} <span class="rn">${escapeHtml(reference)}</span>`
        : escapeHtml(tag.serial);

      return `<div class="tag"><div class="hole"></div>${tag.qr ? qrSvg(tag.qr) : ''}<div class="serial" style="font-size: ${serialMm(tag.code, widthMm)}mm">${code}</div><div class="rule"></div><div class="design">${escapeHtml(tag.design)}</div><div class="variant">${escapeHtml(tag.variant)}</div>${rate}</div>`;
    })
    .join('');

  return { html: page(css, body), widthPt: mmToPt(widthMm), heightPt: mmToPt(heightMm) };
}

/**
 * A QR symbol as SVG — one rectangle per run of dark modules, inside the four-module quiet zone a
 * scanner needs to find the corners. `crispEdges` so a printer never anti-aliases a module away.
 */
export function qrSvg(symbol: QrSymbol): string {
  const quiet = 4;
  const extent = symbol.size + quiet * 2;
  const rects: string[] = [];

  symbol.modules.forEach((row, y) => {
    let start = -1;

    for (let x = 0; x <= row.length; x++) {
      const dark = x < row.length && row[x];

      if (dark && start < 0) {
        start = x;
      } else if (!dark && start >= 0) {
        rects.push(`<rect x="${start + quiet}" y="${y + quiet}" width="${x - start}" height="1"/>`);
        start = -1;
      }
    }
  });

  return `<svg class="qr" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${extent} ${extent}" shape-rendering="crispEdges"><rect width="${extent}" height="${extent}" fill="#fff"/><g fill="#000">${rects.join('')}</g></svg>`;
}

function page(css: string, body: string): string {
  return `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><style>${css}</style></head><body>${body}</body></html>`;
}
